import React, { useEffect, useState, useMemo } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useLang } from "../../contexts/LanguageContext";
import { api } from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { JourneyTimeline } from "../../components/JourneyTimeline";
import ReenrollmentCard from "../../components/ReenrollmentCard";
import { Link, useSearchParams } from "react-router-dom";
import {
  FilePlus, RefreshCw, ArrowRight, BookOpen, CalendarDays, CalendarClock,
  GraduationCap, FileText, MessageSquare, FileCheck, Sparkles, PartyPopper, Wallet,
} from "lucide-react";

export default function ParentDashboard() {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const [apps, setApps] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [, setPayments] = useState([]);
  const [search] = useSearchParams();
  const justArrived = search.get("welcome") === "1";

  useEffect(() => {
    Promise.all([
      api.get("/applications/mine").then(r => setApps(r.data)).catch(() => {}),
      api.get("/enrollments/mine").then(r => setEnrollments(r.data)).catch(() => {}),
      api.get("/payments/mine").then(r => setPayments(r.data)).catch(() => {}),
    ]);
  }, []);

  // ── Plans/installments for the "Mon plan & échéances" hub card ──
  const [paymentPlans, setPaymentPlans] = useState([]);

  // Enrollments that have already been paid (deposit_paid OR status enrolled)
  const paidEnrollments = enrollments.filter(e =>
    e.deposit_paid === true || e.status === "enrolled" || e.deposit_paid_at);

  useEffect(() => {
    api.get("/payments/plan/mine").then(r => setPaymentPlans(r.data?.plans || [])).catch(() => {});
  }, [paidEnrollments.length]);

  // Find the most relevant "next debit" across all enrollments to surface on the hub card
  const nextDebitPreview = useMemo(() => {
    const upcoming = paymentPlans
      .map(p => p.next_installment && ({ ...p.next_installment, student: p.student_name, last4: p.sepa_last4 }))
      .filter(Boolean)
      .sort((a, b) => (a.next_attempt_at || "").localeCompare(b.next_attempt_at || ""));
    return upcoming[0] || null;
  }, [paymentPlans]);
  // Enrollments still missing the availability form
  const pendingAvailability = paidEnrollments.filter(e => !e.availability_submitted_at);
  // Availability submitted, but admin has not yet given the green light → SIS stays locked
  const pendingValidation = paidEnrollments.filter(e =>
    e.availability_submitted_at && !e.student_life_unlocked_at);
  // Enrollments fully ready → student life is unlocked for each of them (admin gave the green light)
  // SIS removed for RGPD compliance — Student Life hub permanently hidden.
  const readyForStudentLife = [];
  const hasStudentLife = false;

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-12 py-12" data-testid="parent-dashboard">
      {/* Header */}
      <div className="mb-10">
        <p className="overline mb-2">{lang === "fr" ? "OFA Portail Familles" : "OFA Family Hub"}</p>
        <h1 className="font-serif text-4xl lg:text-5xl tracking-tighter">
          {lang === "fr" ? "Bonjour, " : "Hello, "}
          {user?.first_name}
        </h1>
        <p className="text-muted-foreground mt-2 text-sm">
          {lang === "fr"
            ? "Retrouvez vos démarches d'admissions et la vie scolaire de votre enfant."
            : "Find your admissions actions and your child's school life."}
        </p>
      </div>

      {/* 🎉 Welcome celebration after submitting availability */}
      {justArrived && hasStudentLife && (
        <Card className="mb-8 border-primary bg-primary/[0.04]" data-testid="welcome-celebration">
          <CardContent className="p-6 flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0">
              <PartyPopper className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <div className="overline text-primary mb-1">
                {lang === "fr" ? "Inscription finalisée" : "Enrollment finalised"}
              </div>
              <h3 className="font-serif text-2xl tracking-tight mb-1">
                {lang === "fr" ? "Bienvenue dans la Vie Scolaire 🎓" : "Welcome to Student Life 🎓"}
              </h3>
              <p className="text-sm text-muted-foreground">
                {lang === "fr"
                  ? "Toutes les étapes sont terminées. Vous accédez maintenant à l'emploi du temps, aux cours, aux bulletins et à la messagerie de votre enfant."
                  : "All steps complete. You now have access to the schedule, courses, report cards and messaging for your child."}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action-required block (re-enrollment, fee_pending, evaluation, accepted) */}
      <ReenrollmentCard />

      {/* ⚠️ Availability still missing for one or more paid enrollments */}
      {pendingAvailability.map(e => {
        const childName = e.student?.first_name || e.student?.name || (lang === "fr" ? "votre enfant" : "your child");
        // Deadline: Aug 25 of the upcoming school year. If we're past Aug 25 already, use next year.
        const today = new Date();
        const yearOfDeadline = (today.getMonth() > 7 || (today.getMonth() === 7 && today.getDate() > 25))
          ? today.getFullYear() + 1
          : today.getFullYear();
        const deadline = new Date(yearOfDeadline, 7, 25); // Aug = month 7 (0-indexed)
        const daysLeft = Math.ceil((deadline - today) / (1000 * 60 * 60 * 24));
        const urgency = daysLeft <= 7 ? "critical" : daysLeft <= 21 ? "urgent" : "normal";
        const urgencyStyles = {
          critical: { border: "border-destructive", bg: "bg-destructive/[0.06]", overline: "text-destructive", dot: "bg-destructive text-white" },
          urgent: { border: "border-amber-500", bg: "bg-amber-50", overline: "text-amber-700", dot: "bg-amber-500 text-white" },
          normal: { border: "border-accent", bg: "bg-accent/[0.04]", overline: "text-accent", dot: "bg-accent text-accent-foreground" },
        }[urgency];
        const deadlineLabel = lang === "fr"
          ? deadline.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })
          : deadline.toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" });
        return (
        <Card key={e.id} className={`mb-4 ${urgencyStyles.border} ${urgencyStyles.bg}`} data-testid={`availability-prompt-${e.id}`}>
          <CardContent className="p-5 flex items-start gap-3">
            <div className={`w-10 h-10 rounded-full ${urgencyStyles.dot} flex items-center justify-center shrink-0`}>
              <CalendarClock className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <div className={`overline ${urgencyStyles.overline} mb-0.5`}>
                {lang === "fr" ? "Votre prochaine étape" : "Your next step"}
              </div>
              <h3 className="font-medium mb-1">
                {lang === "fr"
                  ? <>Remplir le questionnaire de disponibilités pour <strong>{childName}</strong></>
                  : <>Complete the availability questionnaire for <strong>{childName}</strong></>}
              </h3>
              <p className="text-sm text-muted-foreground mb-2">
                {lang === "fr"
                  ? "Indiquez les créneaux où votre enfant est libre, du lundi au vendredi. Nous construisons l'emploi du temps en fonction."
                  : "Tell us when your child is available, Monday to Friday. We'll build the timetable accordingly."}
              </p>
              <div className={`inline-flex items-center gap-1.5 text-xs font-semibold mb-3 ${urgencyStyles.overline}`}>
                <CalendarClock className="w-3.5 h-3.5" />
                {daysLeft > 0
                  ? (lang === "fr"
                      ? <>Il vous reste <strong>{daysLeft} jour{daysLeft > 1 ? "s" : ""}</strong> · échéance le {deadlineLabel}</>
                      : <><strong>{daysLeft} day{daysLeft > 1 ? "s" : ""}</strong> left · due {deadlineLabel}</>)
                  : (lang === "fr"
                      ? <>Échéance dépassée ({deadlineLabel}) — merci de compléter dès que possible</>
                      : <>Deadline passed ({deadlineLabel}) — please complete asap</>)}
              </div>
              <Link to={`/parent/availability/${e.id}`}>
                <Button data-testid={`go-availability-${e.id}`} size="sm" className={`rounded-full ${urgencyStyles.dot} hover:opacity-90`}>
                  {lang === "fr" ? "Compléter maintenant" : "Complete now"} <ArrowRight className="ml-1 w-3.5 h-3.5" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      );})}

      {/* ⏳ Availability sent — waiting for admin green light before SIS opens */}
      {pendingValidation.map(e => {
        const childName = e.student?.first_name || e.student?.name || (lang === "fr" ? "votre enfant" : "your child");
        return (
          <Card key={`pv-${e.id}`} className="mb-4 border-primary/40 bg-primary/[0.04]"
                data-testid={`pending-validation-${e.id}`}>
            <CardContent className="p-5 flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/15 text-primary flex items-center justify-center shrink-0">
                <CalendarClock className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="overline text-primary mb-0.5">
                  {lang === "fr" ? "Disponibilités reçues" : "Availability received"}
                </div>
                <h3 className="font-medium mb-1">
                  {lang === "fr"
                    ? <>Merci ! Nous traitons les créneaux de <strong>{childName}</strong></>
                    : <>Thank you! We&apos;re reviewing <strong>{childName}</strong>&apos;s slots</>}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {lang === "fr"
                    ? "L'équipe OFA construit l'emploi du temps en regroupant votre enfant avec les autres élèves compatibles. La Vie scolaire (emploi du temps, cours, bulletins, messagerie) s'ouvrira automatiquement dès la validation."
                    : "The OFA team is assembling the timetable by grouping your child with compatible students. Student Life (schedule, courses, report cards, messaging) will open automatically once we validate."}
                </p>
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* === ADMISSIONS — one card per child in progress === */}
      {apps.map(app => (
        <Card key={app.id} className="mb-6" data-testid={`app-card-${app.id}`}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm text-muted-foreground">
                {lang === "fr" ? "Candidature" : "Application"} ·{" "}
                <span className="font-medium text-foreground">
                  {app.student?.first_name || app.student_info?.first_name || "—"} {app.student?.last_name || app.student_info?.last_name || ""}
                </span>
              </div>
              <Badge variant="outline">{t(`statuses.${app.status}`) || app.status}</Badge>
            </div>
            <JourneyTimeline status={app.status} />
            {app.status === "evaluation_sent" && app.evaluation && (
              <div className="mt-4">
                <Link to={`/parent/evaluation/${app.id}`}>
                  <Button data-testid={`upload-evaluation-${app.id}`} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full">
                    {lang === "fr" ? "Téléverser mon évaluation" : "Upload my evaluation"}
                  </Button>
                </Link>
              </div>
            )}
            {app.status === "accepted" && (
              <div className="mt-4">
                <Link to={`/parent/enrollment/${app.id}`}>
                  <Button data-testid={`enroll-button-${app.id}`} className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-6">
                    {t("parent.acceptOffer")} <ArrowRight className="ml-1 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      ))}

      {/* === REENROLLMENT INVITATIONS handled via component above ===

       === ADMISSIONS hub === */}
      <SectionHeader
        title={lang === "fr" ? "Admissions" : "Admissions"}
        subtitle={lang === "fr"
          ? "Inscrire un nouvel enfant ou réinscrire un élève déjà à OFA."
          : "Enroll a new child or re-enroll an existing OFA student."}
      />
      <div className="grid md:grid-cols-2 gap-5 mb-12" data-testid="admissions-hub">
        <HubCard
          accent="accent"
          icon={FilePlus}
          title={lang === "fr" ? "Inscrire un (autre) enfant" : "Enroll a (new) child"}
          subtitle={apps.length
            ? (lang === "fr" ? `${apps.length} candidature(s) en cours — démarrer une nouvelle` : `${apps.length} application(s) in progress — start another`)
            : (lang === "fr" ? "Démarrer une candidature" : "Start an application")}
          to="/parent/application/new"
          testid="hub-new-enrollment"
        />
        <HubCard
          accent="accent"
          icon={RefreshCw}
          title={lang === "fr" ? "Réinscription" : "Re-enrollment"}
          subtitle={lang === "fr"
            ? "Pour les élèves déjà à OFA — voir vos invitations ci-dessus."
            : "For existing OFA students — see invitations above."}
          to="#"
          disabled
          testid="hub-reenrollment"
        />
      </div>

      {/* === PAIEMENTS — visible as soon as at least one deposit is paid === */}
      {paidEnrollments.length > 0 && (
        <>
          <SectionHeader
            title={lang === "fr" ? "Paiements" : "Payments"}
            subtitle={lang === "fr"
              ? "Suivez l'acompte, le solde annuel et chaque échéance."
              : "Track the deposit, annual balance and every installment."}
          />
          <div className="grid md:grid-cols-2 gap-5 mb-12" data-testid="payments-hub">
            <HubCard
              accent="primary"
              icon={Wallet}
              title={lang === "fr" ? "Mon plan & échéances" : "My plan & schedule"}
              subtitle={nextDebitPreview ? (
                lang === "fr"
                  ? `Prochain prélèvement : ${new Date(nextDebitPreview.due_date).toLocaleDateString("fr-FR")} · ${nextDebitPreview.amount.toFixed(2)} €${nextDebitPreview.last4 ? ` (IBAN •••• ${nextDebitPreview.last4})` : ""}`
                  : `Next debit: ${new Date(nextDebitPreview.due_date).toLocaleDateString("en-US")} · €${nextDebitPreview.amount.toFixed(2)}${nextDebitPreview.last4 ? ` (IBAN •••• ${nextDebitPreview.last4})` : ""}`
              ) : (
                lang === "fr"
                  ? `${paidEnrollments.length} inscription(s) payée(s) · voir le détail des versements`
                  : `${paidEnrollments.length} paid enrollment(s) · view installments`
              )}
              to="/parent/payment"
              testid="hub-payment-plan"
            />
            <HubCard
              accent="accent"
              icon={FileText}
              title={lang === "fr" ? "Historique de paiements" : "Payment history"}
              subtitle={lang === "fr"
                ? "Tous mes versements passés et à venir, statut SEPA inclus."
                : "All my past and upcoming installments, including SEPA status."}
              to="/parent/payments-history"
              testid="hub-payments-history"
            />
          </div>
        </>
      )}

      {/* === VIE SCOLAIRE — one block per ready enrollment === */}
      {hasStudentLife ? (
        <>
          <SectionHeader
            title={lang === "fr" ? "Vie scolaire" : "Student Life"}
            subtitle={lang === "fr"
              ? "Le quotidien scolaire de votre enfant : emploi du temps, cours, notes, communication, documents."
              : "Your child's day-to-day school life: schedule, courses, grades, communication, documents."}
          />
          {readyForStudentLife.map(e => (
            <div key={e.id} className="mb-8" data-testid={`student-life-${e.student_id}`}>
              <div className="flex items-center gap-2 mb-3 text-sm">
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="font-medium">
                  {e.student?.first_name} {e.student?.last_name} {e.student?.grade ? `· ${e.student.grade}` : ""}
                </span>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                <HubCard accent="primary" icon={CalendarDays}
                  title={lang === "fr" ? "Calendrier" : "Calendar"}
                  subtitle={lang === "fr" ? "Emploi du temps de la semaine" : "Weekly schedule"}
                  to={`/parent/student/${e.student_id}?tab=schedule`} testid={`hub-calendar-${e.student_id}`} />
                <HubCard accent="primary" icon={BookOpen}
                  title={lang === "fr" ? "Choix des cours" : "Course selection"}
                  subtitle={lang === "fr" ? "Cours inscrits et préférences" : "Enrolled courses & preferences"}
                  to="/parent/schedule" testid={`hub-courses-${e.student_id}`} />
                <HubCard accent="primary" icon={GraduationCap}
                  title={lang === "fr" ? "Bulletins & notes" : "Report cards & grades"}
                  subtitle={lang === "fr" ? "Bulletins, transcripts" : "Report cards, transcripts"}
                  to={`/parent/student/${e.student_id}?tab=reports`} testid={`hub-reports-${e.student_id}`} />
                <HubCard accent="primary" icon={MessageSquare}
                  title={lang === "fr" ? "Communication" : "Communication"}
                  subtitle={lang === "fr" ? "Messagerie avec l'école" : "Messaging with the school"}
                  to={`/parent/student/${e.student_id}?tab=messages`} testid={`hub-messages-${e.student_id}`} />
                <HubCard accent="primary" icon={FileCheck}
                  title={lang === "fr" ? "Documents officiels" : "Official documents"}
                  subtitle={lang === "fr" ? "Attestations, contrats, diplômes" : "Certificates, contracts, diplomas"}
                  to={`/parent/student/${e.student_id}?tab=reports`} testid={`hub-documents-${e.student_id}`} />
                <HubCard accent="primary" icon={FileText}
                  title={lang === "fr" ? "Toutes les notes" : "All grades"}
                  subtitle={lang === "fr" ? "Vue détaillée de toutes les notes" : "Detailed view of all grades"}
                  to={`/parent/student/${e.student_id}?tab=grades`} testid={`hub-grades-${e.student_id}`} />
              </div>
            </div>
          ))}
        </>
      ) : (
        <Card className="mb-4 border-dashed border-primary/30 bg-primary/[0.02]" data-testid="zone-student-life-locked">
          <CardContent className="p-6 flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
              <BookOpen className="w-5 h-5" />
            </div>
            <div>
              <Badge variant="outline" className="text-[10px] uppercase tracking-wider text-primary border-primary/40 mb-1">
                {lang === "fr" ? "Vie scolaire" : "Student Life"}
              </Badge>
              <h3 className="font-display font-semibold">
                {pendingValidation.length > 0
                  ? (lang === "fr" ? "En attente du feu vert de l'équipe OFA" : "Awaiting OFA green light")
                  : (lang === "fr" ? "Disponible une fois l'inscription finalisée" : "Available once enrollment is finalised")}
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                {pendingValidation.length > 0
                  ? (lang === "fr"
                      ? "Vos disponibilités sont bien reçues. L'emploi du temps, les cours, les bulletins et la messagerie s'ouvriront ici dès que l'équipe pédagogique aura validé le placement de votre enfant."
                      : "Your availability is in. The schedule, courses, report cards and messaging will open here as soon as the teaching team validates your child's placement.")
                  : (lang === "fr"
                      ? "Après signature du contrat, paiement de l'acompte, envoi des disponibilités et validation par l'équipe OFA, vous accéderez à l'emploi du temps, aux cours, aux bulletins et à la messagerie."
                      : "After signing the contract, paying the deposit, submitting availability and OFA validation, you'll access the schedule, courses, report cards and messaging.")}
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function SectionHeader({ title, subtitle }) {
  return (
    <div className="mb-5">
      <h2 className="font-serif text-2xl tracking-tight">{title}</h2>
      <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
    </div>
  );
}


function HubCard({ accent, icon: Icon, title, subtitle, to, testid, badge, disabled }) {
  const accentClass = accent === "primary"
    ? "hover:border-primary/50 hover:shadow-primary/10"
    : "hover:border-accent/50 hover:shadow-accent/10";
  const iconBg = accent === "primary" ? "bg-primary/10 text-primary" : "bg-accent/10 text-accent";

  const inner = (
    <Card className={`h-full transition-all hover:shadow-lg ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer " + accentClass}`}>
      <CardContent className="p-6 h-full flex flex-col">
        <div className="flex items-start justify-between mb-3">
          <div className={`w-10 h-10 rounded-lg ${iconBg} flex items-center justify-center`}>
            <Icon className="w-5 h-5" strokeWidth={1.7} />
          </div>
          {badge && <Badge variant="outline" className="text-[10px]">{badge}</Badge>}
        </div>
        <div className="font-serif text-xl tracking-tight mb-1">{title}</div>
        <div className="text-xs text-muted-foreground leading-relaxed flex-1">{subtitle}</div>
        {!disabled && <div className={`mt-3 text-xs font-medium ${accent === "primary" ? "text-primary" : "text-accent"} inline-flex items-center`}>
          {Icon === RefreshCw ? "" : "→"}
        </div>}
      </CardContent>
    </Card>
  );

  if (disabled) return <div data-testid={testid}>{inner}</div>;
  return <Link to={to} data-testid={testid}>{inner}</Link>;
}
